import { type UseChatOptions, useChat } from '@ai-sdk/react';
import { type AssistantRuntime, type ThreadMessage, useAuiState } from '@assistant-ui/react';
import { AssistantChatTransport, type UseChatRuntimeOptions, useAISDKRuntime } from '@assistant-ui/react-ai-sdk';
import { encode } from '@toon-format/toon';
import type { UIMessage } from 'ai';

import { formatErrorText, type ToolErrorResponse } from '#plugin-chat/lib/tool/errors/index.ts';
import { ToolOutputErrorSchema } from '#plugin-chat/lib/tool/output-types.ts';
import { AtlasChatTransport } from '#plugin-chat/lib/transport/AtlasChatTransport.tsx';
import { shouldContinueAgentLoop } from '#plugin-chat/lib/transport/agentLoop.ts';
import { createThreadHistoryStore, type StoredMessageRepository } from '#plugin-chat/lib/transport/localStorage.ts';

/**
 * Creates a chat thread runtime with automatic tool execution and error handling.
 * Wraps AI SDK's useChat to detect tool output errors and mark them appropriately.
 */
export function useChatThreadRuntime({
  adapters,
  transport,
  toCreateMessage,
  ...chatOptions
}: UseChatRuntimeOptions<UIMessage> = {}): AssistantRuntime {
  const id = useAuiState(({ threadListItem }) => threadListItem.id);

  // Load persisted messages from localStorage as initial state for useChat.
  // React Compiler will memoize this based on `id` dependency.
  const historyStore = createThreadHistoryStore(window.localStorage, id);
  const stored = historyStore.load();
  const messages =
    stored.messages.length > 0 ? stored.messages.map((m) => m.message as unknown as UIMessage) : undefined;

  // The agentic tool loop is driven by the AI SDK's `sendAutomaticallyWhen`
  // continuation. Stop intent lives on the transport (a long-lived,
  // non-React run-lifecycle owner) so it survives the inter-request gap
  // where the SDK's own `stop()` early-returns and does nothing.
  const stopTransport = transport instanceof AtlasChatTransport ? transport : undefined;

  const chat = useChat({
    // UseChatRuntimeOptions extends UseChatOptions but with stricter generic constraints.
    // The spread works at runtime but TS can't infer the generic parameter compatibility.
    ...(chatOptions as Partial<UseChatOptions<UIMessage>>),
    id,
    transport,
    // Continue the agentic loop only when the assistant completed tool
    // calls AND the user has not requested a stop.
    sendAutomaticallyWhen: ({ messages: loopMessages }) =>
      shouldContinueAgentLoop({
        stopRequested: stopTransport?.stopRequested ?? false,
        messages: loopMessages,
      }),
    messages,
    onFinish: ({ messages: allMessages }) => {
      const store = createThreadHistoryStore(window.localStorage, id);
      const repo: StoredMessageRepository = {
        headId: allMessages.at(-1)?.id ?? null,
        messages: allMessages.map((msg, i) => ({
          message: msg as unknown as ThreadMessage,
          parentId: i > 0 ? (allMessages.at(i - 1)?.id ?? null) : null,
        })),
      };
      store.save(repo);
    },
  });

  // Re-derive the error state for cases the AI SDK doesn't surface.
  //
  // Note: assistant-stream returns "Tool execution was cancelled." with isError: true,
  // but useAISDKRuntime doesn't pass isError to addToolResult, so we re-derive
  // the error state here for cancellation strings and explicit
  // `state: 'output-error'` only.
  //
  // Structured `{ success: false, … }` failure envelopes are deliberately
  // NOT flipped to `output-error`: that state carries only a flat
  // `errorText` string, which collapses the structured envelope (errorType,
  // suggestions, recovery) into pre-formatted prose and forces
  // `render-pipeline`'s ToolProtocolErrorSection to relabel everything
  // `ProtocolError`. Letting them flow as a normal `output-available`
  // output keeps the structured envelope intact so `parseResultEnvelope`
  // → ToolErrorSection renders the real errorType + per-tool `message.error`.
  const wrappedAddToolOutput: typeof chat.addToolOutput = async (output) => {
    const outputValue = output?.output;
    const isCancelledOutput = typeof outputValue === 'string' && outputValue.toLowerCase().includes('cancelled');
    const explicitErrorState = (output as { state?: string })?.state === 'output-error';
    const shouldMarkAsError = explicitErrorState || isCancelledOutput;

    if (shouldMarkAsError) {
      // Render structured tool errors via the shared formatter so the markdown
      // shape (header + message + suggestions) stays in sync with what
      // `ToolExecutionError.toStructuredResponse()` produces. Strings and
      // unstructured objects fall back to verbatim / TOON encoding.
      const renderErrorText = (error: unknown): string => {
        if (typeof error === 'string') {
          return error;
        }
        const parsed = ToolOutputErrorSchema.safeParse(error);
        if (parsed.success) {
          return formatErrorText(parsed.data satisfies ToolErrorResponse);
        }
        return encode(error);
      };
      await chat.addToolOutput({
        tool: output.tool,
        toolCallId: output.toolCallId,
        state: 'output-error',
        errorText: renderErrorText(outputValue),
      });
      return;
    }
    await chat.addToolOutput(output);
  };

  // Shallow copy with overridden methods - avoids immer's deep proxy which fails on frozen arrays.
  //
  // `stop`/`sendMessage`/`regenerate` are the only user-initiated entry
  // points (assistant-ui calls `chat.stop()` on cancel, `chat.sendMessage`
  // on new/edit, `chat.regenerate` on reload). The SDK's loop continuation
  // calls `makeRequest` directly and never passes through them, so setting
  // stop intent here survives the loop while a genuine new turn clears it.
  const wrappedChat = {
    ...chat,
    addToolOutput: wrappedAddToolOutput,
    addToolResult: wrappedAddToolOutput,
    stop: async (): Promise<void> => {
      stopTransport?.requestStop();
      return chat.stop();
    },
    sendMessage: async (...args: Parameters<typeof chat.sendMessage>): Promise<void> => {
      stopTransport?.clearStopRequest();
      return chat.sendMessage(...args);
    },
    regenerate: async (...args: Parameters<typeof chat.regenerate>): Promise<void> => {
      stopTransport?.clearStopRequest();
      return chat.regenerate(...args);
    },
  };

  const runtime = useAISDKRuntime(wrappedChat, {
    adapters,
    ...(toCreateMessage && { toCreateMessage }),
  });

  // Two-way binding: transport needs runtime reference for bidirectional communication
  if (transport instanceof AssistantChatTransport) {
    transport.setRuntime(runtime);
  }

  return runtime;
}
